import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { useOnboarding } from '../../contexts/OnboardingContext'
import {
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  KeyIcon,
  ShieldCheckIcon,
  FolderIcon,
  CpuChipIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline'

const stepIcons: Record<string, React.ElementType> = {
  reddit_app: KeyIcon,
  reddit_auth: ShieldCheckIcon,
  create_project: FolderIcon,
  enable_automation: CpuChipIcon,
}

const OnboardingPanel: React.FC = () => {
  const { state, isPanelOpen, closePanel, dismissOnboarding } = useOnboarding()

  if (!isPanelOpen) return null

  const progressPercentage = (state.completedSteps / state.totalSteps) * 100

  return (
    <AnimatePresence>
      {isPanelOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-900/50 z-40"
            onClick={closePanel}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8 text-white">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <SparklesIcon className="h-8 w-8" />
                  <h2 className="text-2xl font-bold">Getting Started</h2>
                </div>
                <button
                  onClick={closePanel}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <p className="text-white/90 text-sm mb-4">
                Complete these steps to start finding opportunities and engaging with Reddit communities.
              </p>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">Progress</span>
                  <span className="font-semibold">{state.completedSteps} of {state.totalSteps} complete</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2.5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="bg-white rounded-full h-2.5"
                  />
                </div>
              </div>
            </div>

            {/* Steps List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {state.steps.map((step, index) => {
                const Icon = stepIcons[step.id] || ClockIcon
                return (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`border-2 rounded-lg p-4 transition-all ${
                      step.completed
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Step Icon/Number */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        step.completed
                          ? 'bg-green-500 text-white'
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        {step.completed ? (
                          <CheckCircleIcon className="h-6 w-6" />
                        ) : (
                          <Icon className="h-6 w-6" />
                        )}
                      </div>

                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className={`font-semibold ${
                            step.completed ? 'text-green-900' : 'text-gray-900'
                          }`}>
                            {step.title}
                          </h3>
                          {step.completed && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                              Complete
                            </span>
                          )}
                        </div>
                        <p className={`text-sm mb-3 ${
                          step.completed ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {step.description}
                        </p>

                        {/* Action Button */}
                        {!step.completed && (
                          <Link
                            to={step.path}
                            onClick={closePanel}
                            className="inline-flex items-center space-x-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                          >
                            <span>Complete Now</span>
                            <ArrowRightIcon className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-6 space-y-3">
              {state.isComplete ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-green-900 mb-1">All Set!</h3>
                  <p className="text-sm text-green-700">
                    You've completed the setup. Start exploring opportunities!
                  </p>
                </div>
              ) : (
                <button
                  onClick={dismissOnboarding}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Dismiss Tutorial
                </button>
              )}

              <p className="text-xs text-gray-500 text-center">
                You can restart this guide anytime from your profile menu.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default OnboardingPanel
